import { Dispatch, SetStateAction } from "react";
import { createFieldSetStateAction, createStore } from "@tellescope/store";

export type AvailableSidebarContentType = "ContactDetails";
export interface ContentTypeData {
  ContactDetails?: {
    enduserId: string;
    selectedViewId?: string;
  };
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

export const SIDEBAR_MIN_WIDTH = 250;
export const SIDEBAR_MAX_WIDTH = 600;
export const SIDEBAR_DEFAULT_WIDTH = 400;

interface SidebarStore {
  isOpen: boolean;
  contentType: AvailableSidebarContentType | undefined;
  width: number;
  toggleIsOpen: () => void;
  openSidebar: <T extends AvailableSidebarContentType>(contentType: T, data: ContentTypeData[T]) => void;
  setContentType: Dispatch<SetStateAction<AvailableSidebarContentType | undefined>>;
  setWidth: (width: number) => void;
  contentTypeData: ContentTypeData;
  setContentTypeData: Dispatch<SetStateAction<ContentTypeData>>;
}

export const { useStore: useSidebarStore, useShallowStore: useSidebarShallowStore } = createStore<SidebarStore>(
  (set) => ({
    isOpen: false,
    contentType: undefined,
    width: SIDEBAR_MIN_WIDTH,
    toggleIsOpen: () =>
      set((state) => {
        return {
          isOpen: !state.isOpen,
        };
      }),
    openSidebar: (contentType, data) =>
      set((state) => {
        const previous = state.contentTypeData.ContactDetails;
        return {
          isOpen: true,
          contentType,
          contentTypeData: {
            ...state.contentTypeData,
            ContactDetails: data && {
              selectedViewId: previous?.selectedViewId,
              ...data,
            },
          },
        };
      }),
    setContentType: createFieldSetStateAction(set, "contentType"),
    setWidth: (width) =>
      set({
        width: Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, width)),
      }),
    contentTypeData: {
      ContactDetails: {
        enduserId: "",
      },
    },
    setContentTypeData: createFieldSetStateAction(set, "contentTypeData"),
  }),
  {
    persist: "localStorage",
    name: "SIDEBAR_STATE",
    version: 3,
    migrate: (persistedState: unknown, version: number) => {
      let state = isRecord(persistedState) ? persistedState : {};

      if (version < 2) {
        const { width } = state;
        state = {
          ...state,
          width: typeof width === "number" ? Math.max(width, SIDEBAR_DEFAULT_WIDTH) : SIDEBAR_DEFAULT_WIDTH,
        };
      }

      if (version < 3) {
        const { contentTypeData, ...rest } = state;
        const { ContactDetails, ...otherContentTypes } = isRecord(contentTypeData) ? contentTypeData : {};
        const contactDetails = isRecord(ContactDetails) ? ContactDetails : {};
        const enduserId = contactDetails.enduserId;

        state = {
          ...rest,
          contentTypeData: {
            ...otherContentTypes,
            ...(typeof enduserId === "string" && enduserId ? { ContactDetails: { enduserId } } : {}),
          },
        };
      }

      return state;
    },
  }
);
